/**
 * Filters for reports.
 */
import * as React from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { SearchInput } from '@/components/forms/SearchInput';
import {
  REPORT_STATUS_CONFIG,
  REPORT_RATING_FILTER_VALUES,
  SESSION_TYPE_CONFIG,
  DATE_RANGE_PERIOD_OPTIONS,
  type ReportInPersonTab as Tab,
} from '@/components/reports/report-configs';
import type { ReportFilters } from '@/types/inperson/report.types';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import {
  formatReportFilterDateToString,
  getReportSessionTypeFilterKeys,
  parseReportFilterDateInput,
} from './report-table-utils';

/** Props for {@link ReportFiltersContent}. */
interface ReportFiltersContentProps {
  filters: ReportFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  activeTab?: Tab | 'all';
  onToggleStatus: (status: string) => void;
  onToggleRating: (rating: number) => void;
  onSetDateRange: (range: ReportFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onToggleSessionType: (type: string) => void;
  onSetEstablishment: (id: string | undefined) => void;
  onSetSearch?: (value: string | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Preset period chips for in-person report filters. */
const PeriodFilter: React.FC<{
  filters: ReportFilters;
  onSetDateRange: (range: ReportFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetDateRange, onSetCustomDateRange }) => (
  <div className="filter-group">
    <span className="filter-group-label">Période</span>
    <SegmentedControl
      size="sm"
      value={filters.customDateFrom ? '' : (filters.dateRange ?? 'all')}
      onChange={(v) => {
        onSetDateRange(v as ReportFilters['dateRange']);
        onSetCustomDateRange(undefined, undefined);
      }}
      options={[...DATE_RANGE_PERIOD_OPTIONS]}
    />
  </div>
);

/** Custom from/to date range for in-person report filters. */
const DateRangeFilter: React.FC<{
  filters: ReportFilters;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetDateRange: (range: ReportFilters['dateRange']) => void;
}> = ({ filters, onSetCustomDateRange, onSetDateRange }) => {
  const fromStr = formatReportFilterDateToString(filters.customDateFrom);
  const toStr = formatReportFilterDateToString(filters.customDateTo);

  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.customDateTo);
      return;
    }
    onSetCustomDateRange(parseReportFilterDateInput(val, false), filters.customDateTo);
    onSetDateRange('all');
  };

  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.customDateFrom, undefined);
      return;
    }
    onSetCustomDateRange(filters.customDateFrom, parseReportFilterDateInput(val, true));
    onSetDateRange('all');
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date personnalisée</span>
      <div className="w-auto mt-0.5">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Report status chips (draft hidden in the dashboard bar). */
const StatusFilter: React.FC<{
  filters: ReportFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut</span>
    <div className="filter-options">
      {/* Hide draft in the dashboard filter bar; draft still appears on rows/cards via REPORT_STATUS_CONFIG */}
      {Object.entries(REPORT_STATUS_CONFIG)
        .filter(([key]) => key !== 'draft')
        .map(([key, config]) => {
          const Icon = config.icon;
          const isActive = filters.statuses.includes(key);
          return (
            <Button
              key={key}
              variant="custom"
              size="sm"
              icon={Icon}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                isActive ? 'filter-badge-active' : 'filter-badge-inactive',
              )}
              style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
              onClick={() => onToggleStatus(key)}
            >
              {config.label}
            </Button>
          );
        })}
    </div>
  </div>
);

/** Star-rating chips for in-person report filters. */
const RatingFilter: React.FC<{
  filters: ReportFilters;
  onToggleRating: (rating: number) => void;
}> = ({ filters, onToggleRating }) => (
  <div className="filter-group">
    <span className="filter-group-label">Note</span>
    <div className="filter-options">
      {REPORT_RATING_FILTER_VALUES.map((rating) => {
        const isActive = filters.ratings.includes(rating);
        return (
          <Button
            key={rating}
            variant="custom"
            size="sm"
            icon={Star}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isActive ? 'filter-option-tertiary--active' : 'filter-option-tertiary',
            )}
            onClick={() => onToggleRating(rating)}
          >
            {rating}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Session-type chips scoped to the active reports tab. */
const SessionTypeFilter: React.FC<{
  filters: ReportFilters;
  onToggleSessionType: (type: string) => void;
  activeTab?: Tab | 'all';
}> = ({ filters, onToggleSessionType, activeTab }) => {
  const typesForTab = React.useMemo(() => getReportSessionTypeFilterKeys(activeTab), [activeTab]);

  if (typesForTab.length === 0) return null;

  return (
    <div className="filter-group">
      <span className="filter-group-label">Type de séance</span>
      <div className="filter-options">
        {typesForTab.map((key) => {
          const config = SESSION_TYPE_CONFIG[key as keyof typeof SESSION_TYPE_CONFIG];
          if (!config) return null;
          const Icon = config.icon;
          const isActive = filters.sessionTypes?.includes(key) ?? false;
          return (
            <Button
              key={key}
              variant="custom"
              size="sm"
              icon={Icon}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                isActive ? 'filter-badge-active' : 'filter-badge-inactive',
              )}
              style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
              onClick={() => onToggleSessionType(key)}
            >
              {config.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Establishment autocomplete for in-person report filters. */
const EstablishmentFilter: React.FC<{
  filters: ReportFilters;
  onSetEstablishment: (id: string | undefined) => void;
}> = ({ filters, onSetEstablishment }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          options={options}
          value={filters.establishmentIds?.[0] ?? ''}
          onChange={(val) => onSetEstablishment(val || undefined)}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Free-text search for in-person report filters. */
const SearchFilter: React.FC<{
  filters: ReportFilters;
  onSetSearch: (value: string | undefined) => void;
}> = ({ filters, onSetSearch }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-56">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder="Nom, prénom..."
        value={filters.search ?? ''}
        onSearch={(q) => onSetSearch(q.trim() || undefined)}
        onClear={() => onSetSearch(undefined)}
      />
    </div>
  </div>
);

/** Filter fields for the in-person reports list. */
export const ReportFiltersContent: React.FC<ReportFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  activeTab,
  onToggleStatus,
  onToggleRating,
  onSetDateRange,
  onSetCustomDateRange,
  onToggleSessionType,
  onSetEstablishment,
  onSetSearch,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-search') && onSetSearch && (
        <SearchFilter filters={filters} onSetSearch={onSetSearch} />
      )}

      {show('filter-period') && (
        <PeriodFilter
          filters={filters}
          onSetDateRange={onSetDateRange}
          onSetCustomDateRange={onSetCustomDateRange}
        />
      )}

      {show('filter-date-range') && (
        <DateRangeFilter
          filters={filters}
          onSetCustomDateRange={onSetCustomDateRange}
          onSetDateRange={onSetDateRange}
        />
      )}

      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}

      {show('filter-rating') && <RatingFilter filters={filters} onToggleRating={onToggleRating} />}

      {show('filter-type-session') && (
        <SessionTypeFilter
          filters={filters}
          onToggleSessionType={onToggleSessionType}
          activeTab={activeTab}
        />
      )}

      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onSetEstablishment={onSetEstablishment} />
      )}
    </div>
  );
};
